import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { useContact } from "@/components/useContent";
import { Check, Mail, Phone, ArrowRight } from "lucide-react";

const TEAM = [
  {
    id: "heather",
    name: "Heather Da Boa",
    role: "Co-Founder · Lead Artist · Executive Producer & Creative Director",
    photo: "/founder-heather.jpg",
    bio: [
      "Heather Da Boa is the co-founder, lead recording artist, and creative visionary behind DGE: Cold-Blooded Records LLC™. Known for her versatile fusion of melodic trap, trap-soul, R&B, and raw hip-hop storytelling, she is the architect of the trademarked Cold-Blooded Soundscapes™ identity.",
      "Her journey as an independent powerhouse gained decisive momentum through her partnership with manager and mentor Sherrod \"HotRod\" Whiteside. What began as a transformative artist-manager dynamic evolved through deep friendship and shared vision into a co-founding executive partnership to establish DGE: Cold-Blooded Records LLC™.",
      "As a hands-on artist, songwriter, producer, and audio engineer, Heather Da Boa drives the label's sonic innovation, catalog development, and enterprise brand ecosystem — including the official retail destination Boa's 33rd Dimension™ and lifestyle apparel lines Cold-Blooded Culture Attire™, Slither N Sparkle™, and Slitherin' Soles™.",
    ],
    focus: [
      "Creative Direction & Brand Strategy",
      "Music Production, Engineering & Songwriting",
      "Cold-Blooded Soundscapes™ Sound Architecture",
      "Merchandise & E-Commerce Operations",
    ],
  },
  {
    id: "hotrod",
    name: "Sherrod \"HotRod\" Whiteside",
    role: "Co-Founder · Executive Partner · Business Manager & Mentor",
    photo: "/founder-hotrod.jpg",
    bio: [
      "Sherrod \"HotRod\" Whiteside is a visionary talent discoverer, manager, and Executive Partner at DGE: Cold-Blooded Records LLC™. Widely recognized for discovering and mentoring flagship artist Heather Da Boa, HotRod transformed a powerful creative alliance into a thriving independent music power.",
      "From manager and mentor to co-founding business partner, HotRod provides the strategic direction, operational discipline, and business acumen that drive the label's corporate vision. Grounded in mutual trust and a commitment to artist ownership, he co-founded DGE: Cold-Blooded Records LLC™ alongside Heather Da Boa to build a lasting framework for independent artists.",
      "As Co-Founder and Executive Partner, HotRod leads business management, catalog ownership strategy, contract development, and commercial distribution. He continues to serve as an artist development coach and executive strategist, pushing the label's signature sound into new regional and national markets.",
    ],
    focus: [
      "Label Operations & Business Management",
      "Artist Development & Performance Coaching",
      "Contract Strategy & Distribution",
      "Strategic Partnerships & Catalog Ownership",
    ],
  },
];

export default function TeamPage() {
  const contact = useContact();
  useEffect(() => {
    document.title = "Executive Leadership | DGE: Cold-Blooded Records LLC™";
    return () => { document.title = "DGE: Cold-Blooded Records LLC™ | Official ASCAP® Member Label — Est. 2026"; };
  }, []);

  return (
    <main className="bg-obsidian text-ice min-h-screen" data-testid="team-page">
      <Navbar />
      <div className="pt-[70px]">
        <section className="relative py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
            <Reveal className="max-w-3xl">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-venom font-medium mb-4">
                Meet the Staff
              </p>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight" data-testid="team-headline">
                Executive Leadership
                <br />
                <span className="text-stroke-ice">&amp; Founders</span>
              </h1>
              <p className="mt-5 text-sm sm:text-base text-ice/70 leading-relaxed">
                Underground power meets structured business excellence. The two executives behind
                Cold-Blooded Soundscapes™.
              </p>
            </Reveal>

            <div className="mt-14 grid lg:grid-cols-2 gap-8 lg:gap-10" data-testid="team-grid">
              {TEAM.map((member, i) => (
                <Reveal key={member.id} delay={i * 0.12}>
                  <article
                    className="group h-full bg-charcoal border border-crystal/25 rounded-2xl overflow-hidden hover:border-venom/50 hover:-translate-y-1 hover:shadow-[0_16px_50px_rgba(57,255,20,0.12)] transition-all duration-500 flex flex-col"
                    data-testid={`team-card-${member.id}`}
                  >
                    <div className="relative aspect-[4/5] overflow-hidden">
                      <img
                        src={member.photo}
                        alt={`Portrait of ${member.name}`}
                        loading="lazy"
                        className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/20 to-transparent" />
                      <span className="absolute bottom-4 left-5 font-mono text-[10px] uppercase tracking-[0.18em] bg-obsidian/85 backdrop-blur-sm border border-venom/50 text-venom rounded-full px-3.5 py-1.5">
                        {member.role}
                      </span>
                    </div>

                    <div className="p-6 sm:p-8 flex flex-col flex-1">
                      <h2 className="font-display text-xl sm:text-2xl font-bold text-white">{member.name}</h2>

                      <div className="mt-4 space-y-3.5">
                        {member.bio.map((para, j) => (
                          <p key={j} className="text-sm text-ice/70 leading-relaxed">{para}</p>
                        ))}
                      </div>

                      <div className="mt-6 border-t border-white/10 pt-5">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-crystal mb-3">
                          Key Focus Areas
                        </p>
                        <ul className="space-y-2">
                          {member.focus.map((f) => (
                            <li key={f} className="flex items-start gap-2.5 text-sm text-ice/75">
                              <Check size={15} className="text-venom shrink-0 mt-0.5" />
                              {f}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="mt-6 pt-5 border-t border-white/10 flex flex-wrap gap-3">
                        <a
                          href={`mailto:${contact.email}?subject=${encodeURIComponent(`Attention: ${member.name}`)}`}
                          className="inline-flex items-center gap-2 text-xs font-semibold border border-crystal/50 text-ice/80 rounded-full px-4 py-2 hover:border-venom hover:text-venom transition-all duration-300"
                          data-testid={`team-contact-email-${member.id}`}
                        >
                          <Mail size={13} /> Email the Label
                        </a>
                        <a
                          href={`tel:${contact.phones[0].replace(/[^0-9]/g, "")}`}
                          className="inline-flex items-center gap-2 text-xs font-semibold border border-crystal/50 text-ice/80 rounded-full px-4 py-2 hover:border-venom hover:text-venom transition-all duration-300"
                          data-testid={`team-contact-phone-${member.id}`}
                        >
                          <Phone size={13} /> {contact.phones[0]}
                        </a>
                      </div>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.2}>
              <div className="mt-16 lg:mt-20 text-center bg-charcoal border border-crystal/25 rounded-3xl px-6 sm:px-12 py-12 sm:py-16 relative overflow-hidden" data-testid="team-cta">
                <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[420px] h-[220px] bg-venom/12 blur-[100px] rounded-full" aria-hidden="true" />
                <div className="relative">
                  <h2 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold text-white tracking-tight">
                    Connect with DGE: Cold-Blooded Records LLC™
                  </h2>
                  <p className="mt-3 text-sm sm:text-base text-ice/60 max-w-xl mx-auto leading-relaxed">
                    For booking, distribution inquiries, production licensing, or artist development
                    consultations.
                  </p>
                  <Link
                    to="/quote"
                    className="group mt-7 inline-flex items-center gap-2.5 bg-venom text-obsidian font-bold text-sm sm:text-base px-8 py-4 rounded-full hover:glow-venom hover:scale-[1.03] active:scale-95 transition-all duration-300"
                    data-testid="team-cta-button"
                  >
                    Contact Label Management
                    <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      </div>
      <Footer />
    </main>
  );
}
